import axios, { AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from "axios";
import { env } from "@astro/config";
import type { ApiError } from "@astro/types";
import { ApiRequestError } from "./errors";
import { handleUnauthorized } from "./interceptors";

export interface RequestOptions {
  headers?: Record<string, string>;
  /** Query-string parameters. */
  params?: Record<string, unknown>;
  signal?: AbortSignal;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/**
 * Shared axios instance. All data traffic goes through the auth BFF's /api
 * proxy, so the httpOnly session cookie (credentials) is the only auth the
 * browser ever handles — the Laravel Bearer token stays server-side.
 */
export const http = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-App": env.VITE_APP_NAME,
  },
});

/** Laravel 422 payloads carry field errors under `errors`. */
interface UpstreamErrorBody extends Partial<ApiError> {
  errors?: Record<string, string[]>;
}

http.interceptors.response.use(undefined, async (error: AxiosError) => {
  // Cancelled requests (e.g. React Query aborting a stale fetch when the
  // query key changes) aren't real errors — rethrow as-is so callers'
  // cancellation handling (React Query's AbortController integration) sees
  // the original cancel signal instead of a wrapped ApiRequestError.
  if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
    throw error;
  }

  const config = error.config as RetriableConfig | undefined;

  // 401 → give auth a chance to refresh the session, then replay once.
  if (error.response?.status === 401 && config && !config._retried && (await handleUnauthorized())) {
    config._retried = true;
    config.headers = AxiosHeaders.from(config.headers);
    return http.request(config);
  }

  const body = (error.response?.data ?? {}) as UpstreamErrorBody;
  throw new ApiRequestError({
    code: body.code ?? (error.response ? "http_error" : "network_error"),
    message: body.message ?? error.message,
    status: error.response?.status ?? 0,
    details: body.details ?? body.errors,
  });
});

async function request<T>(
  method: string,
  path: string,
  opts: RequestOptions & { body?: unknown } = {}
): Promise<T> {
  const { body, ...rest } = opts;
  const res = await http.request<T>({ method, url: path, data: body, ...rest });
  return res.data;
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PATCH", path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, opts),
};
